/* eslint-disable indent */
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { initializeUser } from '../reducers/userReducer'
import Spinner from 'react-bootstrap/Spinner'
import { Card, Row, Col } from 'react-bootstrap'
import TeamTable from './TeamTable'
import SpecTable from './SpecTable'
import Results from './Results'
import { getTranslation } from '../services/translations'

const homeLayout = [
  ['RatingRightDef', 'RatingMidDef', 'RatingLeftDef'],
  ['', 'RatingMidfield', ''],
  ['RatingRightAtt', 'RatingMidAtt', 'RatingLeftAtt'],
  ['RatingIndirectSetPiecesDef', 'RatingIndirectSetPiecesAtt'],
]
const awayLayout = [
  ['RatingLeftAtt', 'RatingMidAtt', 'RatingRightAtt'],
  ['', 'RatingMidfield', ''],
  ['RatingLeftDef', 'RatingMidDef', 'RatingRightDef'],
  ['RatingIndirectSetPiecesDef', 'RatingIndirectSetPiecesAtt'],
]

const matchPageUrl = match => {
  const isHto =
    String(match?.SourceSystem).toLowerCase() === 'htointegrated' ||
    Number(match?.MatchType) > 12
  const path = `/Club/Matches/Match.aspx?matchID=${match?.MatchID || ''}${
    isHto ? '&SourceSystem=HTOIntegrated' : ''
  }`
  return `https://www.hattrick.org/goto.ashx?path=${encodeURIComponent(path)}`
}

const formatMatchDate = value => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value || ''
  return [
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    date.getFullYear(),
  ].join('.')
}

const defenceRatingFields = [
  'RatingRightDef',
  'RatingMidDef',
  'RatingLeftDef',
]
const attackRatingFields = [
  'RatingRightAtt',
  'RatingMidAtt',
  'RatingLeftAtt',
]

const roundRating = value => Math.round(value * 4) / 4

const getRatingBalanceMultiplier = (field, balance, side) => {
  const directionalBalance = side === 'home' ? balance : -balance
  const amount = Math.abs(directionalBalance) / 100
  if (directionalBalance === 0) return 1

  const isDefensive = directionalBalance > 0
  if ((isDefensive && defenceRatingFields.includes(field)) ||
      (!isDefensive && attackRatingFields.includes(field))) {
    return 1 + (isDefensive ? 0.128 : 0.074) * amount
  }
  if ((isDefensive && attackRatingFields.includes(field)) ||
      (!isDefensive && defenceRatingFields.includes(field))) {
    return 1 - (isDefensive ? 0.118 : 0.12) * amount
  }

  return 1
}

const applyRatingBalance = (team, balance, sourceBalance, side) => {
  if (balance === sourceBalance) return team
  const adjustedTeam = { ...team }
  const ratingFields = [...defenceRatingFields, ...attackRatingFields]

  ratingFields.forEach(field => {
    adjustedTeam[field] = roundRating(
      (Number(team[field]) * getRatingBalanceMultiplier(field, balance, side)) /
        getRatingBalanceMultiplier(field, sourceBalance, side)
    )
  })

  return adjustedTeam
}

const createDefaultTeam = layout =>
  Object.fromEntries(
    layout.flat().filter(Boolean).map(field => [field, 1])
  )

const defaultActiveRoleIds = [100, 101, 103, 105, 106, 107, 108, 109, 110, 111, 113]
const defaultActivePositions = () =>
  Object.fromEntries(defaultActiveRoleIds.map(roleId => [roleId, true]))

const lineupRoleIds = activePositions =>
  Object.keys(activePositions).filter(roleId => activePositions[roleId])

const tacticsTable = {
  0: 'Normal',
  1: 'Pressing',
  2: 'CA',
  3: 'AIM',
  4: 'AOW',
  7: 'Creative',
  8: 'LS',
}

const pastedTactics = {
  normal: 0,
  normaali: 0,
  'counter-attacks': 2,
  vastahyokkaykset: 2,
  'attack in the middle': 3,
  'luova peli': 7,
  pressing: 1,
  painostus: 1,
  'counter-attacking': 2,
  vastahyokkays: 2,
  'attack in middle': 3,
  hyokkayskeskeltä: 3,
  'attack on wings': 4,
  hyokkayslaidoilta: 4,
  'play creatively': 7,
  luova: 7,
  'long shots': 8,
  kaukolaukaukset: 8,
  'hyokkays keskelta': 3,
  'hyokkays keskeltä': 3,
  'hyokkays laidoilta': 4,
}

const pastedPlayStyles = {
  neutral: 0,
  neutraali: 0,
  '100% defensive': -100,
  '100% puolustava': -100,
  '100% offensive': 100,
  '100% hyokkaava': 100,
  '100% hyÃ¶kkÃ¤Ã¤vÃ¤': 100,
}

const parsePastedRatings = text => {
  const getValues = labels => {
    const row = text.match(
      new RegExp(`\\[th\\](?:${labels.join('|')})\\[/th\\]([\\s\\S]*?)\\[/tr\\]`, 'i')
    )
    if (!row) return []
    return [...row[1].matchAll(/\[td[^\]]*\]([0-9]+(?:[.,][0-9]+)?)\[\/td\]/gi)].map(
      match => Number(match[1].replace(',', '.'))
    )
  }

  const defence = getValues(['Defence', 'Puolustus'])
  const midfield = getValues(['Midfield', 'Keskikenttä'])
  const attack = getValues(['Attack', 'Hyokkays', 'Hyökkäys'])
  if (defence.length !== 3 || midfield.length !== 1 || attack.length !== 3) {
    throw new Error('Arvosanoja ei tunnistettu leikepoydalta.')
  }

  const tacticMatch = text.match(
    /\[b\](?:Tactics|Taktiikat)\[\/b\]:\s*([^\r\n]+)/i
  )
  const tacticText = tacticMatch
    ? tacticMatch[1]
        .trim()
        .toLocaleLowerCase('fi')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    : ''
  const tacticType = Object.entries(pastedTactics).find(([tactic]) =>
    tacticText.startsWith(tactic)
  )?.[1]
  const tacticSkill = tacticText.match(/\((\d+)\)\s*$/)?.[1]
  const playStyleMatch = text.match(
    /\[b\](?:Pelitapa|Style of Play)\[\/b\]:\s*([^\r\n]+)/i
  )
  const playStyleText = playStyleMatch
    ? playStyleMatch[1]
        .trim()
        .toLocaleLowerCase('fi')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    : ''
  const pastedPercentage = Number(
    playStyleText.match(/([0-9]+(?:[.,][0-9]+)?)\s*%/)?.[1]?.replace(',', '.')
  )
  const playStyle = Number.isNaN(pastedPercentage)
    ? Object.entries(pastedPlayStyles).find(([style]) =>
        playStyleText.startsWith(style)
      )?.[1]
    : playStyleText.includes('defensive') ||
        playStyleText.includes('puolustava')
      ? -pastedPercentage
      : playStyleText.includes('offensive') ||
          playStyleText.includes('hyokkaava')
        ? pastedPercentage
        : undefined

  const tournamentMatchId = text.match(/\[tournamentmatchid=(\d+)\]/i)?.[1]

  return {
    matchId: text.match(/\[matchid=(\d+)\]/i)?.[1] || tournamentMatchId || '',
    isHto: Boolean(tournamentMatchId),
    ratings: {
      RatingRightDef: defence[0],
      RatingMidDef: defence[1],
      RatingLeftDef: defence[2],
      RatingMidfield: midfield[0],
      RatingRightAtt: attack[0],
      RatingMidAtt: attack[1],
      RatingLeftAtt: attack[2],
    },
    tacticType,
    tacticSkill: tacticSkill === undefined ? undefined : Number(tacticSkill),
    playStyle,
  }
}

const Simulator = () => {
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const { matchId: simulatorMatchId } = useParams()
  const navigate = useNavigate()
  const lang = user?.language || 'fi'
  const t = key => getTranslation(lang, key)

  useEffect(() => {
    dispatch(initializeUser())
  }, [dispatch])

  const [homeMatches, setHomeMatches] = useState([])
  const [awayMatches, setAwayMatches] = useState([])
  const [activeHome, setActiveHome] = useState(() =>
    createDefaultTeam(homeLayout)
  )
  const [activeAway, setActiveAway] = useState(() =>
    createDefaultTeam(awayLayout)
  )
  const [selectedHomeId, setSelectedHomeId] = useState('')
  const [selectedAwayId, setSelectedAwayId] = useState('')
  const [homeSpecs, setHomeSpecs] = useState({})
  const [awaySpecs, setAwaySpecs] = useState({})
  const [homeStaminas, setHomeStaminas] = useState({})
  const [awayStaminas, setAwayStaminas] = useState({})
  const [homeBehaviours, setHomeBehaviours] = useState({})
  const [awayBehaviours, setAwayBehaviours] = useState({})
  const [homeActivePositions, setHomeActivePositions] = useState(defaultActivePositions)
  const [awayActivePositions, setAwayActivePositions] = useState(defaultActivePositions)
  const [cupExtraTime, setCupExtraTime] = useState(false)
  const [manualMatchId, setManualMatchId] = useState('')
  const [leagueId, setLeagueId] = useState('')
  const [leagueSeason, setLeagueSeason] = useState('')
  const [showOwnLeagueTeams, setShowOwnLeagueTeams] = useState(false)
  const [loadingOwnLeagueTeamId, setLoadingOwnLeagueTeamId] = useState('')
  const [homeTeamId, setHomeTeamId] = useState('')
  const [awayTeamId, setAwayTeamId] = useState('')
  const [simulateResults, setSimulateResults] = useState(undefined)
  const [homeTacticType, setHomeTacticType] = useState(0)
  const [homeTacticSkill, setHomeTacticSkill] = useState(1)
  const [awayTacticType, setAwayTacticType] = useState(0)
  const [awayTacticSkill, setAwayTacticSkill] = useState(1)
  const [homeRatingBalance, setHomeRatingBalance] = useState(0)
  const [awayRatingBalance, setAwayRatingBalance] = useState(0)
  const [homeRatingSourceBalance, setHomeRatingSourceBalance] = useState(0)
  const [awayRatingSourceBalance, setAwayRatingSourceBalance] = useState(0)
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [upcomingMatches, setUpcomingMatches] = useState([])
  const [showUpcomingMatches, setShowUpcomingMatches] = useState(false)
  const [selectedUpcomingMatchId, setSelectedUpcomingMatchId] = useState('')
  const [showSimulatorGuide, setShowSimulatorGuide] = useState(false)
  const [isLoadingTeam, setIsLoadingTeam] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [pasteMessage, setPasteMessage] = useState('')

  useEffect(() => {
    if (!loadError && !pasteMessage) return undefined

    const timeoutId = setTimeout(() => {
      setLoadError('')
      setPasteMessage('')
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [loadError, pasteMessage])

  const teams = Array.isArray(user?.teams) ? user.teams : []
  const upcomingMatchesByTeam = upcomingMatches.reduce((groups, match) => {
    const teamId = String(match.SimulatorTeamID)
    if (!groups[teamId]) {
      groups[teamId] = {
        name: match.SimulatorTeamName || `Joukkue ${teamId}`,
        matches: [],
      }
    }
    groups[teamId].matches.push(match)
    return groups
  }, {})

  const getSpecsFromLineup = lineupArr => {
    if (!Array.isArray(lineupArr)) return {}
    const specs = {}
    lineupArr.forEach(player => {
      if (player.RoleID !== undefined && player.Specialty !== undefined) {
        // Powerful (3) vain tietyillä ehdoilla
        if (player.Specialty === 3) {
          // Forward (111,112,113): vain jos Behaviour === '0'
          if (
            [111, 112, 113].includes(Number(player.RoleID)) &&
            String(player.Behaviour) !== '0'
          ) {
            return // Älä lisää Powerfulia hyökkääjälle jos ei Behaviour 0
          }
          // Inner (107,108,109): vain puolustava nuoli (Behaviour === '2')
          if (
            [107, 108, 109].includes(Number(player.RoleID)) &&
            String(player.Behaviour) !== '2'
          ) {
            return // Älä lisää Powerfulia innerille jos ei Behaviour 2
          }
        }
        specs[player.RoleID] = Number(player.Specialty)
      }
    })
    return specs
  }

  const handleHomeTacticTypeChange = val => setHomeTacticType(val)
  const handleHomeTacticSkillChange = val => setHomeTacticSkill(val)
  const handleAwayTacticTypeChange = val => setAwayTacticType(val)
  const handleAwayTacticSkillChange = val => setAwayTacticSkill(val)
  const handleRatingValueChange = (side, field, value) => {
    const balance = side === 'home' ? homeRatingBalance : awayRatingBalance
    const sourceBalance =
      side === 'home' ? homeRatingSourceBalance : awayRatingSourceBalance
    const baseValue = roundRating(
      (value * getRatingBalanceMultiplier(field, sourceBalance, side)) /
        getRatingBalanceMultiplier(field, balance, side)
    )

    if (side === 'home') {
      setActiveHome(prev => ({ ...prev, [field]: baseValue }))
    } else {
      setActiveAway(prev => ({ ...prev, [field]: baseValue }))
    }
  }

  const getLineupValues = (lineupArr, key, fallback) => {
    if (!Array.isArray(lineupArr)) return {}
    return Object.fromEntries(
      lineupArr
        .filter(player => player.RoleID !== undefined)
        .map(player => [player.RoleID, Number(player[key]) || fallback])
    )
  }

  const setLineupDetails = (side, lineup) => {
    const specs = getSpecsFromLineup(lineup)
    const staminas = getLineupValues(lineup, 'Stamina', 8)
    const behaviours = getLineupValues(lineup, 'Behaviour', 0)
    const activePositions = Object.fromEntries(
      (Array.isArray(lineup) ? lineup : [])
        .filter(player => player.RoleID !== undefined)
        .map(player => [player.RoleID, true])
    )
    if (side === 'home') {
      setHomeSpecs(specs)
      setHomeStaminas(staminas)
      setHomeBehaviours(behaviours)
      setHomeActivePositions(activePositions)
    } else {
      setAwaySpecs(specs)
      setAwayStaminas(staminas)
      setAwayBehaviours(behaviours)
      setAwayActivePositions(activePositions)
    }
  }

  const handleTeamSelect = async (teamId, matchId = '') => {
    if (!teamId || !user?.oauth_token) return

    try {
      setSelectedTeamId(String(teamId))
      if (!matchId) {
        setSelectedUpcomingMatchId('')
        setUpcomingMatches([])
      }
      setIsLoadingTeam(true)
      setLoadError('')
      setHomeMatches([])
      setAwayMatches([])
      if (!matchId) {
        const res = await axios.get(
          `/api/simulator/upcomingmatches/${user.oauth_token}?teamId=${encodeURIComponent(
            teamId
          )}`
        )
        const futureMatches = res.data.upcomingMatches || []
        if (futureMatches.length === 0) {
          setLoadError('Tulevia otteluita ei lÃƒÂ¶ytynyt valitulle joukkueelle.')
          return
        }
        setUpcomingMatches(futureMatches)
        return
      }
      const res = await axios.get(
        `/api/simulator/matches/${user.oauth_token}?teamId=${encodeURIComponent(
          teamId
        )}&matchId=${encodeURIComponent(matchId)}`
      )
      setSelectedUpcomingMatchId(String(matchId))
      const userMatches = res.data.UserMatches || []
      const opponentMatches = res.data.OpponentMatches || []
      const nextMatch = res.data.nextMatch

      if (!nextMatch || userMatches.length === 0 || opponentMatches.length === 0) {
        setLoadError('Ottelu- tai arvosanatietoja ei lÃ¶ytynyt valitulle joukkueelle.')
        return
      }

      const firstUserMatch = userMatches[0]
      const firstOpponentMatch = opponentMatches[0]
      const userIsHomeTeam =
        String(nextMatch.HomeTeam.HomeTeamID) === String(teamId)

      if (userIsHomeTeam) {
        setSelectedHomeId(firstUserMatch.MatchID)
        setActiveHome(firstUserMatch)
        setHomeMatches(userMatches)
        setLineupDetails('home', firstUserMatch.Lineup)
        setHomeTacticType(Number(firstUserMatch.TacticType) || 0)
        setHomeTacticSkill(Number(firstUserMatch.TacticSkill) || 1)
        setSelectedAwayId(firstOpponentMatch.MatchID)
        setActiveAway(firstOpponentMatch)
        setAwayMatches(opponentMatches)
        setLineupDetails('away', firstOpponentMatch.Lineup)
        setAwayTacticType(Number(firstOpponentMatch.TacticType) || 0)
        setAwayTacticSkill(Number(firstOpponentMatch.TacticSkill) || 1)
      } else {
        setSelectedHomeId(firstOpponentMatch.MatchID)
        setActiveHome(firstOpponentMatch)
        setHomeMatches(opponentMatches)
        setLineupDetails('home', firstOpponentMatch.Lineup)
        setHomeTacticType(Number(firstOpponentMatch.TacticType) || 0)
        setHomeTacticSkill(Number(firstOpponentMatch.TacticSkill) || 1)
        setSelectedAwayId(firstUserMatch.MatchID)
        setActiveAway(firstUserMatch)
        setAwayMatches(userMatches)
        setLineupDetails('away', firstUserMatch.Lineup)
        setAwayTacticType(Number(firstUserMatch.TacticType) || 0)
        setAwayTacticSkill(Number(firstUserMatch.TacticSkill) || 1)
      }
    } catch (error) {
      console.error(error)
      setLoadError('Joukkueen ottelutietojen lataus epÃ¤onnistui.')
    } finally {
      setIsLoadingTeam(false)
    }
  }

  const handleLoadOwnMatches = async () => {
    if (!user?.oauth_token) return
    try {
      setIsLoadingTeam(true)
      setLoadError('')
      setSelectedTeamId('')
      setSelectedUpcomingMatchId('')
      setUpcomingMatches([])
      setShowUpcomingMatches(false)
      const res = await axios.get(
        `/api/simulator/upcomingmatches/${user.oauth_token}`
      )
      const futureMatches = res.data.upcomingMatches || []
      if (futureMatches.length === 0) {
        setLoadError('Tulevia otteluita ei lÃƒÂ¶ytynyt omilta joukkueilta.')
        return
      }
      setUpcomingMatches(futureMatches)
      setShowUpcomingMatches(true)
    } catch (error) {
      console.error(error)
      setLoadError('Omien tulevien otteluiden lataus epÃƒÂ¤onnistui.')
    } finally {
      setIsLoadingTeam(false)
    }
  }

  const applyPastedRatings = async (side, text) => {
    try {
      const pasted = parsePastedRatings(text)
      const updateTeam = team => ({
        ...team,
        ...pasted.ratings,
        ...(pasted.matchId ? { MatchID: pasted.matchId } : {}),
      })

      if (side === 'home') {
        setActiveHome(updateTeam)
        if (pasted.tacticType !== undefined) setHomeTacticType(pasted.tacticType)
        if (pasted.tacticSkill !== undefined) setHomeTacticSkill(pasted.tacticSkill)
        if (pasted.playStyle !== undefined) {
          setHomeRatingBalance(-pasted.playStyle)
          setHomeRatingSourceBalance(-pasted.playStyle)
        }
      } else {
        setActiveAway(updateTeam)
        if (pasted.tacticType !== undefined) setAwayTacticType(pasted.tacticType)
        if (pasted.tacticSkill !== undefined) setAwayTacticSkill(pasted.tacticSkill)
        if (pasted.playStyle !== undefined) {
          setAwayRatingBalance(pasted.playStyle)
          setAwayRatingSourceBalance(pasted.playStyle)
        }
      }
      if (pasted.matchId) {
        setManualMatchId(pasted.matchId)
        const pastedIsHto = String(pasted.matchId).length === 8
        const opponent = await axios.get(
          `/api/simulator/opponent/${user.oauth_token}/${pasted.matchId}?hto=${pastedIsHto}`
        )
        if (side === 'home') {
          setAwayTeamId(String(opponent.data.awayTeamId))
        } else {
          setHomeTeamId(String(opponent.data.homeTeamId))
        }
      }
      setPasteMessage(
        `Arvosanat liitetty ${side === 'home' ? 'kotijoukkueelle' : 'vierasjoukkueelle'}.`
      )
    } catch (error) {
      console.error(error)
      setPasteMessage(error.message || 'Arvosanojen liittaminen epaonnistui.')
    }
  }

  const handlePasteRatings = async side => {
    try {
      if (!navigator.clipboard?.readText) {
        throw new Error('Leikepoydan luku ei ole selaimessa kaytettavissa.')
      }
      await applyPastedRatings(side, await navigator.clipboard.readText())
    } catch (error) {
      console.error(error)
      setPasteMessage(error.message || 'Arvosanojen liittaminen epaonnistui.')
    }
  }

  const handlePasteField = (event, side) => {
    event.preventDefault()
    applyPastedRatings(side, event.clipboardData.getData('text'))
  }

  useEffect(() => {
    if (selectedHomeId && homeMatches.length > 0) {
      const match = homeMatches.find(m => m.MatchID === selectedHomeId)
      if (match) {
        setActiveHome(match)
        setHomeRatingBalance(0)
        setHomeRatingSourceBalance(0)
        setLineupDetails('home', match.Lineup)
        setHomeTacticType(Number(match.TacticType) || 0)
        setHomeTacticSkill(Number(match.TacticSkill) || 1)
      }
    }
  }, [selectedHomeId, homeMatches])

  useEffect(() => {
    if (selectedAwayId && awayMatches.length > 0) {
      const match = awayMatches.find(m => m.MatchID === selectedAwayId)
      if (match) {
        setActiveAway(match)
        setAwayRatingBalance(0)
        setAwayRatingSourceBalance(0)
        setLineupDetails('away', match.Lineup)
        setAwayTacticType(Number(match.TacticType) || 0)
        setAwayTacticSkill(Number(match.TacticSkill) || 1)
      }
    }
  }, [selectedAwayId, awayMatches])

  const handleSimulate = async () => {
    try {
      setSimulateResults(null)
      const res = await axios.post(`/api/simulator/${user.oauth_token}`, {
        HomeTeam: {
          ...applyRatingBalance(
            activeHome,
            homeRatingBalance,
            homeRatingSourceBalance,
            'home'
          ),
          Lineup: lineupRoleIds(homeActivePositions).map(RoleID => ({
            RoleID: Number(RoleID),
            Specialty: homeSpecs[RoleID] || 0,
            Stamina: homeStaminas[RoleID] || 8,
            Behaviour: homeBehaviours[RoleID] || 0,
          })),
          TacticType: homeTacticType,
          TacticSkill: homeTacticSkill,
          Specialties: homeSpecs,
        },
        AwayTeam: {
          ...applyRatingBalance(
            activeAway,
            awayRatingBalance,
            awayRatingSourceBalance,
            'away'
          ),
          Lineup: lineupRoleIds(awayActivePositions).map(RoleID => ({
            RoleID: Number(RoleID),
            Specialty: awaySpecs[RoleID] || 0,
            Stamina: awayStaminas[RoleID] || 8,
            Behaviour: awayBehaviours[RoleID] || 0,
          })),
          TacticType: awayTacticType,
          TacticSkill: awayTacticSkill,
          Specialties: awaySpecs,
        },
        CupExtraTime: cupExtraTime,
      })
      //console.log(res.data)
      setSimulateResults(res.data)
    } catch (e) {
      console.error(e)
    }
  }

  const handleManualSimulate = async matchIdInput => {
    const suppliedMatchId =
      typeof matchIdInput === 'string' || typeof matchIdInput === 'number'
        ? matchIdInput
        : manualMatchId
    const matchId = String(suppliedMatchId).trim()
    if (!/^\d{8,9}$/.test(matchId)) return

    const isHto = matchId.length === 8
    try {
      setSimulateResults(null)
      const url = `/api/simulator/${user.oauth_token}/${matchId}${
        isHto ? '?hto=true' : ''
      }`
      const res = await axios.get(url)
      //console.log(res.data)
      const homeTeam = {
        ...res.data.HomeTeam,
        MatchID: matchId,
        Opponent: res.data.AwayTeam.AwayTeamName || res.data.AwayTeam.HomeTeamName,
      }
      const awayTeam = {
        ...res.data.AwayTeam,
        MatchID: matchId,
        Opponent: res.data.HomeTeam.HomeTeamName || res.data.HomeTeam.AwayTeamName,
      }
      setSimulateResults(res.data)
      setManualMatchId(matchId)
      setActiveHome(homeTeam)
      setActiveAway(awayTeam)
      setLineupDetails('home', homeTeam.Lineup)
      setLineupDetails('away', awayTeam.Lineup)
      setHomeTacticType(Number(homeTeam.TacticType) || 0)
      setHomeTacticSkill(Number(homeTeam.TacticSkill) || 1)
      setAwayTacticType(Number(awayTeam.TacticType) || 0)
      setAwayTacticSkill(Number(awayTeam.TacticSkill) || 1)
      setHomeMatches([homeTeam])
      setAwayMatches([awayTeam])
      setSelectedHomeId(matchId)
      setSelectedAwayId(matchId)
      setHomeTeamId(res.data.HomeTeam.HomeTeamID || '')
      setAwayTeamId(res.data.AwayTeam.AwayTeamID || '')
    } catch (e) {
      console.error(e)
      const message =
        e.response?.status === 409
          ? e.response?.data?.error || 'Ottelua ei ole vielä pelattu.'
          : 'Ottelun simulointi epäonnistui.'
      setSimulateResults(message)
      setLoadError(message)
    }
  }

  const handleLeagueSimulate = () => {
    const normalizedLeagueId = String(leagueId).trim()
    const normalizedSeason = String(leagueSeason).trim()
    if (!/^\d+$/.test(normalizedLeagueId)) return
    navigate(`/simulator/L-${normalizedLeagueId}${
      /^\d+$/.test(normalizedSeason) ? `?season=${normalizedSeason}` : ''
    }`)
  }

  const handleOwnLeagueSimulate = async team => {
    const teamId = String(
      typeof team === 'object' ? team.teamID : team
    ).trim()
    if (!/^\d+$/.test(teamId) || !user?.oauth_token) return

    try {
      setLoadingOwnLeagueTeamId(teamId)
      const response = await axios.get(
        `/api/simulator/team-league/${user.oauth_token}?teamId=${teamId}`
      )
      const leagueLevelUnitId = Number(response.data?.leagueLevelUnitId)
      if (!Number.isFinite(leagueLevelUnitId)) {
        throw new Error('League ID is missing')
      }
      navigate(`/simulator/L-${leagueLevelUnitId}`)
    } catch (error) {
      console.error('Own league simulation failed:', error)
      setLoadError('Oman joukkueen sarjaa ei voitu ladata.')
    } finally {
      setLoadingOwnLeagueTeamId('')
    }
  }

  useEffect(() => {
    if (!simulatorMatchId || !user?.oauth_token) return
    handleManualSimulate(simulatorMatchId)
    // The URL and authenticated user are the only triggers; including the
    // handler would rerun the simulation after every state update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulatorMatchId, user?.oauth_token])

  const handleHomeTeamChange = async () => {
    if (!homeTeamId) return
    try {
      const res = await axios.get(
        `/api/simulator/singlematch/${homeTeamId}/${user.oauth_token}`
      )
      // Tee jotain tuloksella, esim. päivitä activeHome, homeSpecs jne.
      //console.log(res.data)
      setSelectedHomeId(res.data.SingleMatches[0].MatchID)
      setActiveHome(res.data.SingleMatches[0])
      setHomeMatches(res.data.SingleMatches || [])
      setLineupDetails('home', res.data.SingleMatches[0].Lineup)
      setHomeTacticType(Number(res.data.SingleMatches[0].TacticType) || 0)
      setHomeTacticSkill(Number(res.data.SingleMatches[0].TacticSkill) || 1)
    } catch (e) {
      console.error(e)
    }
  }

  const handleAwayTeamChange = async () => {
    if (!awayTeamId) return
    try {
      const res = await axios.get(
        `/api/simulator/singlematch/${awayTeamId}/${user.oauth_token}`
      )
      // Tee jotain tuloksella, esim. päivitä activeAway, awaySpecs jne.
      //console.log(res.data)
      setSelectedAwayId(res.data.SingleMatches[0].MatchID)
      setActiveAway(res.data.SingleMatches[0])
      setAwayMatches(res.data.SingleMatches || [])
      setLineupDetails('away', res.data.SingleMatches[0].Lineup)
      setAwayTacticType(Number(res.data.SingleMatches[0].TacticType) || 0)
      setAwayTacticSkill(Number(res.data.SingleMatches[0].TacticSkill) || 1)
    } catch (e) {
      console.error(e)
    }
  }

  // Näytä spinner kunnes käyttäjä on ladattu
  const teamSelector = (
    <div style={{ marginBottom: 16 }}>
      {upcomingMatches.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowUpcomingMatches(show => !show)}
          disabled={isLoadingTeam}
        >
          {showUpcomingMatches ? 'Piilota omat ottelut' : 'Näytä omat ottelut'}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleLoadOwnMatches}
          disabled={isLoadingTeam || teams.length === 0}
        >
          Lataa omat ottelut
        </button>
      )}
      {true ? (
        [].map(team => {
          const teamId = String(
            typeof team === 'object' ? team.teamID : team
          )
          const teamName =
            typeof team === 'object' && team.teamName
              ? team.teamName
              : `Joukkue ${teamId}`
          return (
            <button
              key={teamId}
              type="button"
              onClick={() => handleTeamSelect(teamId)}
              disabled={isLoadingTeam}
              style={{
                marginRight: 8,
                marginBottom: 8,
                fontWeight: selectedTeamId === teamId ? 'bold' : 'normal',
              }}
            >
              {teamName}
            </button>
          )
        })
      ) : (
        <span>Joukkueita ei lÃ¶ytynyt.</span>
      )}
      {false && upcomingMatches.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 6 }}>Valitse tuleva ottelu:</div>
          {upcomingMatches.map(match => {
            const isHome =
              String(match.HomeTeam?.HomeTeamID) === String(selectedTeamId)
            const opponent = isHome ? match.AwayTeam : match.HomeTeam
            const opponentName =
              opponent?.AwayTeamName || opponent?.HomeTeamName || 'Vastustaja'
            return (
              <button
                key={match.MatchID}
                type="button"
                onClick={() => handleTeamSelect(selectedTeamId, match.MatchID)}
                disabled={isLoadingTeam}
                style={{
                  marginRight: 8,
                  marginBottom: 8,
                  fontWeight:
                    selectedUpcomingMatchId === String(match.MatchID)
                      ? 'bold'
                      : 'normal',
                }}
              >
                {match.MatchDate} — {opponentName} {isHome ? '(H)' : '(A)'}
              </button>
            )
          })}
        </div>
      ) : null}
      {false && upcomingMatches.length > 0 ? (
        <table style={{ marginTop: 8, width: '100%' }}>
          <thead>
            <tr>
              <th>Joukkue</th>
              <th>Päivä</th>
              <th>Vastustaja</th>
              <th>Paikka</th>
            </tr>
          </thead>
          <tbody>
            {upcomingMatches.map(match => {
              const teamId = String(match.SimulatorTeamID)
              const isHome = String(match.HomeTeam?.HomeTeamID) === teamId
              const opponent = isHome ? match.AwayTeam : match.HomeTeam
              const opponentName =
                opponent?.AwayTeamName || opponent?.HomeTeamName || 'Vastustaja'
              return (
                <tr
                  key={`${teamId}-${match.MatchID}`}
                  onClick={() => handleTeamSelect(teamId, match.MatchID)}
                  style={{
                    cursor: 'pointer',
                    fontWeight:
                      selectedUpcomingMatchId === String(match.MatchID)
                        ? 'bold'
                        : 'normal',
                  }}
                >
                  <td>{match.SimulatorTeamName || `Joukkue ${teamId}`}</td>
                  <td>{match.MatchDate}</td>
                  <td>{opponentName}</td>
                  <td>{isHome ? 'Koti' : 'Vieras'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : null}
      {showUpcomingMatches && upcomingMatches.length > 0 ? (
        <table style={{ marginTop: 8, width: '100%' }}>
          <thead>
            <tr>
              {Object.entries(upcomingMatchesByTeam).map(([teamId, team]) => (
                <th key={teamId}>{team.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {Object.entries(upcomingMatchesByTeam).map(([teamId, team]) => (
                <td key={teamId} style={{ verticalAlign: 'top' }}>
                  {team.matches.map(match => {
                    const isHome =
                      String(match.HomeTeam?.HomeTeamID) === teamId
                    const opponent = isHome ? match.AwayTeam : match.HomeTeam
                    const opponentName =
                      opponent?.AwayTeamName ||
                      opponent?.HomeTeamName ||
                      'Vastustaja'
                    return (
                      <div
                        key={match.MatchID}
                        onClick={() =>
                          !isLoadingTeam && handleTeamSelect(teamId, match.MatchID)
                        }
                        role="button"
                        tabIndex={0}
                        style={{
                          display: 'block',
                          width: '100%',
                          marginBottom: 4,
                          padding: '6px 8px',
                          borderRadius: 4,
                          border: '1px solid #4b5563',
                          background: '#2c2f33',
                          cursor: isLoadingTeam ? 'wait' : 'pointer',
                          opacity: isLoadingTeam ? 0.6 : 1,
                          fontWeight:
                            selectedUpcomingMatchId === String(match.MatchID)
                              ? 'bold'
                              : 'normal',
                        }}
                      >
                        {formatMatchDate(match.MatchDate)}: {opponentName} ({isHome ? 'H' : 'A'})
                      </div>
                    )
                  })}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      ) : null}
    </div>
  )

  if (!user || !user._loaded) {
    return (
      <Card style={{ backgroundColor: '#2C2F33', color: '#FFFFFF' }}>
        <Card.Body>
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card
      className="simulator-page"
      style={{
        backgroundColor: '#2C2F33',
        color: '#FFFFFF',
        borderColor: '#1E2124',
      }}
    >
      <Card.Body>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ marginBottom: 16 }}>Simulator</h2>
          <button
            type="button"
            className="simulator-guide-toggle"
            onClick={() => setShowSimulatorGuide(show => !show)}
            aria-label={t('simulatorGuideInfo')}
            aria-expanded={showSimulatorGuide}
            title={t('simulatorGuideInfo')}
          >
            ℹ
          </button>
        </div>
        {showSimulatorGuide ? <div
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            border: '1px solid #46515c',
            borderRadius: 5,
            background: '#23272A',
            fontSize: '0.9rem',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {t('simulatorGuideTitle')}
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>{t('simulatorGuideLoad')}</li>
            <li>{t('simulatorGuidePositions')}</li>
            <li>{t('simulatorGuideRatings')}</li>
            <li>{t('simulatorGuideRun')}</li>
          </ul>
        </div> : null}
        {teamSelector}
        {isLoadingTeam ? <Spinner animation="border" /> : null}
        {loadError || pasteMessage ? (
          <div className="simulator-notice">{loadError || pasteMessage}</div>
        ) : null}
        {/* Kotijoukkueen TeamTable ja SpecTable vierekkäin */}
        <Card
          style={{
            backgroundColor: '#23272A',
            color: '#FFFFFF',
            borderColor: '#6ECDEA',
            marginBottom: 16,
          }}
        >
          <Card.Header className="simulator-team-header">
            <span>
              Hometeam:{' '}
              {activeHome.HomeTeamName || activeHome.AwayTeamName || ''} vs:{' '}
              <label>
                <select
                  value={selectedHomeId}
                  onChange={e => setSelectedHomeId(e.target.value)}
                  style={{ minWidth: 120 }}
                >
                  {Array.isArray(homeMatches) ? (
                    homeMatches.map(m => (
                      <option key={m.MatchID} value={m.MatchID}>
                        {m.Opponent}{' '}
                        {m.MatchSide === 'home' ? '(H)' : '(A)'}{' '}
                        {m.Formation}
                      </option>
                    ))
                  ) : (
                    <option value={homeMatches.MatchID}>
                      {homeMatches.Opponent}
                    </option>
                  )}
                </select>
              </label>{' '}
              <a
                href={matchPageUrl(activeHome)}
                target="_blank"
                rel="noreferrer"
              >
                ({activeHome.MatchID})
              </a>
            </span>
            <span
              className="simulator-team-actions"
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 8,
              }}
            >
              <span style={{ display: 'none' }}>
              <button
                type="button"
                onClick={() => handlePasteRatings('home')}
                style={{ marginRight: 8 }}
              >
                Liitä arvosanat
              </button>
              <input
                type="text"
                value=""
                onChange={() => {}}
                onPaste={event => handlePasteField(event, 'home')}
                placeholder="Ctrl+V"
                aria-label="Liitä kotijoukkueen arvosanat"
                style={{
                  width: 90,
                  flex: '0 0 90px',
                  overflow: 'hidden',
                  textOverflow: 'clip',
                  whiteSpace: 'nowrap',
                  marginRight: 8,
                }}
              />
              </span>
              <input
                type="text"
                value={homeTeamId}
                onChange={e => setHomeTeamId(e.target.value)}
                placeholder="Team ID"
                style={{ width: 110, marginRight: 8 }}
              />
              <button onClick={handleHomeTeamChange}>Change team</button>
            </span>
          </Card.Header>
          <Card.Body>
            <Row style={{ display: 'flex', alignItems: 'stretch' }}>
              <Col
                md={5}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <TeamTable
                  className="simulator-ratings-table"
                  team={applyRatingBalance(
                    activeHome,
                    homeRatingBalance,
                    homeRatingSourceBalance,
                    'home'
                  )}
                  layout={homeLayout}
                  onValueChange={(field, value) =>
                    handleRatingValueChange('home', field, Number(value))
                  }
                  tableStyle={{ height: '100%' }}
                  tacticsTable={tacticsTable}
                  tacticType={homeTacticType}
                  tacticSkill={homeTacticSkill}
                  onTacticTypeChange={handleHomeTacticTypeChange}
                  onTacticSkillChange={handleHomeTacticSkillChange}
                  ratingBalance={homeRatingBalance}
                  onRatingBalanceChange={setHomeRatingBalance}
                  side="home"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={() => handlePasteRatings('home')}>
                    Liitä arvosanat
                  </button>
                  <input
                    type="text"
                    value=""
                    onChange={() => {}}
                    onPaste={event => handlePasteField(event, 'home')}
                    placeholder="Ctrl+V"
                    aria-label="Liitä kotijoukkueen arvosanat"
                    style={{ width: 90 }}
                  />
                </div>
              </Col>
              <Col
                md={7}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <SpecTable
                  className="simulator-spec-table"
                  playerSpecs={homeSpecs}
                  onSpecChange={(roleId, spec) =>
                    setHomeSpecs(prev => ({ ...prev, [roleId]: spec }))
                  }
                  playerStaminas={homeStaminas}
                  playerBehaviours={homeBehaviours}
                  activePositions={homeActivePositions}
                  onPositionActivate={roleId =>
                    setHomeActivePositions(prev => ({ ...prev, [roleId]: true }))
                  }
                  onPositionDeactivate={roleId =>
                    setHomeActivePositions(prev => ({ ...prev, [roleId]: false }))
                  }
                  onStaminaChange={(roleId, stamina) =>
                    setHomeStaminas(prev => ({ ...prev, [roleId]: stamina }))
                  }
                  onBehaviourChange={(roleId, behaviour) =>
                    setHomeBehaviours(prev => ({ ...prev, [roleId]: behaviour }))
                  }
                  activeColor="#6ECDEA"
                  side="home"
                  tableStyle={{ height: '100%' }}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {/* Vierasjoukkueen TeamTable ja SpecTable vierekkäin */}
        <Card
          style={{
            backgroundColor: '#23272A',
            color: '#FFFFFF',
            borderColor: '#D15E5E',
            marginBottom: 16,
          }}
        >
          <Card.Header className="simulator-team-header">
            <span>
              Awayteam:{' '}
              {activeAway.HomeTeamName || activeAway.AwayTeamName || ''} vs:{' '}
              <label>
                <select
                  value={selectedAwayId}
                  onChange={e => setSelectedAwayId(e.target.value)}
                  style={{ minWidth: 120 }}
                >
                  {Array.isArray(awayMatches) ? (
                    awayMatches.map(m => (
                      <option key={m.MatchID} value={m.MatchID}>
                        {m.Opponent}{' '}
                        {m.MatchSide === 'away' ? '(A)' : '(H)'}{' '}
                        {m.Formation}
                      </option>
                    ))
                  ) : (
                    <option value={awayMatches.MatchID}>
                      {awayMatches.Opponent}
                    </option>
                  )}
                </select>
              </label>{' '}
              <a
                href={matchPageUrl(activeAway)}
                target="_blank"
                rel="noreferrer"
              >
                ({activeAway.MatchID})
              </a>
            </span>
            <span
              className="simulator-team-actions"
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 8,
              }}
            >
              <span style={{ display: 'none' }}>
              <button
                type="button"
                onClick={() => handlePasteRatings('away')}
                style={{ marginRight: 8 }}
              >
                Liitä arvosanat
              </button>
              <input
                type="text"
                value=""
                onChange={() => {}}
                onPaste={event => handlePasteField(event, 'away')}
                placeholder="Ctrl+V"
                aria-label="Liitä vierasjoukkueen arvosanat"
                style={{
                  width: 90,
                  flex: '0 0 90px',
                  overflow: 'hidden',
                  textOverflow: 'clip',
                  whiteSpace: 'nowrap',
                  marginRight: 8,
                }}
              />
              </span>
              <input
                type="text"
                value={awayTeamId}
                onChange={e => setAwayTeamId(e.target.value)}
                placeholder="Team ID"
                style={{ width: 110, marginRight: 8 }}
              />
              <button onClick={handleAwayTeamChange}>Change team</button>
            </span>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={5}>
                <TeamTable
                  className="simulator-ratings-table"
                  team={applyRatingBalance(
                    activeAway,
                    awayRatingBalance,
                    awayRatingSourceBalance,
                    'away'
                  )}
                  layout={awayLayout}
                  onValueChange={(field, value) =>
                    handleRatingValueChange('away', field, Number(value))
                  }
                  tacticsTable={tacticsTable}
                  tacticType={awayTacticType}
                  tacticSkill={awayTacticSkill}
                  onTacticTypeChange={handleAwayTacticTypeChange}
                  onTacticSkillChange={handleAwayTacticSkillChange}
                  ratingBalance={awayRatingBalance}
                  onRatingBalanceChange={setAwayRatingBalance}
                  side="away"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={() => handlePasteRatings('away')}>
                    Liitä arvosanat
                  </button>
                  <input
                    type="text"
                    value=""
                    onChange={() => {}}
                    onPaste={event => handlePasteField(event, 'away')}
                    placeholder="Ctrl+V"
                    aria-label="Liitä vierasjoukkueen arvosanat"
                    style={{ width: 90 }}
                  />
                </div>
              </Col>
              <Col md={7}>
                <SpecTable
                  className="simulator-spec-table"
                  playerSpecs={awaySpecs}
                  onSpecChange={(roleId, spec) =>
                    setAwaySpecs(prev => ({ ...prev, [roleId]: spec }))
                  }
                  playerStaminas={awayStaminas}
                  playerBehaviours={awayBehaviours}
                  activePositions={awayActivePositions}
                  onPositionActivate={roleId =>
                    setAwayActivePositions(prev => ({ ...prev, [roleId]: true }))
                  }
                  onPositionDeactivate={roleId =>
                    setAwayActivePositions(prev => ({ ...prev, [roleId]: false }))
                  }
                  onStaminaChange={(roleId, stamina) =>
                    setAwayStaminas(prev => ({ ...prev, [roleId]: stamina }))
                  }
                  onBehaviourChange={(roleId, behaviour) =>
                    setAwayBehaviours(prev => ({ ...prev, [roleId]: behaviour }))
                  }
                  activeColor="#D15E5E"
                  side="away"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {/* Vetovalikot ja napit */}
        <Row className="g-3 align-items-stretch">
          {/* Valinnat ja napit */}
          <Col md={4} className="d-flex">
            <Card
              style={{
                backgroundColor: '#23272A',
                color: '#FFFFFF',
                borderColor: '#7289DA',
                marginBottom: 16,
                width: '100%',
                minHeight: 150,
              }}
            >
              <Card.Body style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                ></div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 60,
                  }}
                >
                  {/* Simulate Match -nappi keskellä */}
                  <button
                    onClick={handleSimulate}
                    className="simulator-primary-action"
                    style={{ marginRight: 16, height: 40 }}
                  >
                    Simulate Match
                  </button>
                  <label>
                    <input
                      type="checkbox"
                      checked={cupExtraTime}
                      onChange={event => setCupExtraTime(event.target.checked)}
                      style={{ marginRight: 4 }}
                    />
                    Cup extra time
                  </label>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="d-flex">
            <Card
              style={{
                backgroundColor: '#23272A',
                color: '#FFFFFF',
                borderColor: '#7289DA',
                marginBottom: 16,
                width: '100%',
                minHeight: 150,
              }}
            >
              <Card.Body style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div>
                  <label>
                    Simulate with match ID:&nbsp;
                    <input
                      type="text"
                      value={manualMatchId}
                      onChange={e => setManualMatchId(e.target.value)}
                      style={{ width: 120, marginRight: 8 }}
                      placeholder="MatchID"
                    />
                  </label>
                  <button onClick={() => handleManualSimulate()}>Simulate</button>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="d-flex">
            <Card
              style={{
                backgroundColor: '#23272A',
                color: '#FFFFFF',
                borderColor: '#7289DA',
                marginBottom: 16,
                width: '100%',
                minHeight: 150,
              }}
            >
              <Card.Body style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Simulate with league ID:&nbsp;
                    <input
                      type="text"
                      value={leagueId}
                      onChange={e => setLeagueId(e.target.value)}
                      style={{ width: 120, marginRight: 8 }}
                      placeholder="League ID"
                    />
                  </label>
                  <label>
                    Kausi:&nbsp;
                    <input
                      type="text"
                      value={leagueSeason}
                      onChange={e => setLeagueSeason(e.target.value)}
                      style={{ width: 70, marginRight: 8 }}
                      placeholder="Kausi"
                    />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={handleLeagueSimulate}>Simuloi sarja</button>
                  <button onClick={() => setShowOwnLeagueTeams(value => !value)}>
                    Lataa omat
                  </button>
                </div>
                {showOwnLeagueTeams && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    {teams.map((team, index) => {
                      const teamId = String(typeof team === 'object' ? team.teamID : team)
                      const teamName = typeof team === 'object' && team.teamName
                        ? team.teamName
                        : `Joukkue ${teamId || index + 1}`
                      return (
                        <button
                          key={teamId || index}
                          disabled={loadingOwnLeagueTeamId === teamId}
                          onClick={() => handleOwnLeagueSimulate(team)}
                        >
                          {loadingOwnLeagueTeamId === teamId ? 'Ladataan...' : teamName}
                        </button>
                      )
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Card
          style={{
            backgroundColor: '#23272A',
            color: '#FFFFFF',
            borderColor: '#43B581',
            marginBottom: 16,
            minHeight: 180,
          }}
        >
          <Card.Body>
            <h5>
              {activeHome.HomeTeamName || activeHome.AwayTeamName || 'Home team'} -{' '}
              {activeAway.HomeTeamName || activeAway.AwayTeamName || 'Away team'}
            </h5>
            <Results simulateResults={simulateResults} />
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  )
}

export default Simulator
